/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { ProductCardPLP } from '@/components/content/CatalogEntryList/parts/ProductCardPLP';
import { ProductCardPLPSkeleton } from '@/components/content/CatalogEntryList/parts/ProductCardPLPSkeleton';
import { ContentRecommendation } from '@/components/content/ContentRecommendation';
import { useBreadcrumbTrail } from '@/data/Content/BreadcrumbTrail';
import { useCatalogEntryList } from '@/data/Content/CatalogEntryList';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { useLocalization } from '@/data/Localization';
import { useSettings } from '@/data/Settings';
import { ContentContext } from '@/data/context/content';
import { EventsContext } from '@/data/context/events';
import { Grid, Skeleton, Typography } from '@mui/material';
import { FC, Fragment, useContext, useEffect, useState } from 'react';

export const CatalogEntryListProductGrid: FC = () => {
	const { products, loading, selectedFacet } = useContext(ContentContext) as ReturnType<
		typeof useCatalogEntryList
	>;
	const router = useNextRouter();
	const { onSearchResultsView, onItemListView } = useContext(EventsContext);
	const { settings } = useSettings();
	const {
		Labels: { gtmSearchResults, gtmItemList, noProductsFoundForFilter, searchAgain },
	} = useLocalization('ProductGrid');
	const { searchTerm, facet, minPrice } = router.query;

	const breadcrumb = useBreadcrumbTrail();

	const [lastQuery, setLastQuery] = useState<string>('');

	useEffect(() => {
		if (typeof window !== 'undefined') {
			const url = window.location.href.split('&');
			const lastQuery = url[url.length - 1].split('=')[0];
			setLastQuery(lastQuery);
		}
	}, [router.asPath]);

	useEffect(() => {
		if (products && !loading) {
			const term = Array.isArray(searchTerm) ? searchTerm.join(' ') : (searchTerm as string);
			const listPageName = searchTerm ? gtmSearchResults.t({ term }) : gtmItemList.t();

			onItemListView({ gtm: { products, listPageName, storeName: settings.storeName, settings } });
			if (searchTerm) {
				onSearchResultsView({
					gtm: { searchTerm: term, numberOfResults: products.length, settings },
				});
			}
		}
		if (searchTerm && products.length === 1 && facet === undefined && minPrice === undefined) {
			const product = products[0];
			router.push({ pathname: product?.seo?.href }, undefined, { shallow: true });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [products]);

	const breadcrumbKey = breadcrumb.breadcrumb?.map((item) => item.label).join('-');
	const breadcrumbEmsBase = `DPV2ProductGridCard-${breadcrumbKey}`;

	return (
		<Grid container>
			<Grid
				container
				rowSpacing={2}
				columnSpacing={2}
				alignItems="stretch"
				id="catalog-entry-list-product-grid"
				data-testid="catalog-entry-list-product-grid"
			>
				{loading && lastQuery !== 'facetLimit' ? (
					<>
						{Array.from({ length: 36 }).map((_, index) => (
							<ProductCardPLPSkeleton key={index} />
						))}
					</>
				) : !products.length && !loading ? (
					<Grid item>
						<Typography>
							{searchTerm
								? searchAgain.t({ searchTerm: decodeURIComponent(searchTerm as string) })
								: noProductsFoundForFilter.t()}
						</Typography>
					</Grid>
				) : (
					<>
						{products?.map((product, index: number) =>
							selectedFacet.length === 0 && (index === 5 || index === 11 || index === 21) ? (
								<Fragment key={product.id}>
									<ContentRecommendation
										id={product.id}
										withoutFrame
										properties={{
											emsName: `${breadcrumbEmsBase}-${index}`,
										}}
										loadingElement={
											<Grid item xs={6} md={4} key={product.id} display="flex">
												<Skeleton
													variant="rectangular"
													width="100%"
													height="100%"
													sx={{ borderRadius: 0 }}
												/>
											</Grid>
										}
									/>
									<ProductCardPLP product={product} />
								</Fragment>
							) : (
								<ProductCardPLP key={product.id} product={product} />
							)
						)}
					</>
				)}
			</Grid>
		</Grid>
	);
};
